import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Copy, Send, X, AlertCircle } from 'lucide-react';
import { AIRequest } from '@/types/debug';
import { useDebug } from '@/contexts/debug-context';
import { useSimpleToast } from '@/hooks/use-simple-toast';

interface PromptEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  request: AIRequest | null;
  onSend: (modifiedRequest: AIRequest) => void;
  onCancel: () => void;
}

export function PromptEditModal({ 
  isOpen, 
  onClose, 
  request, 
  onSend, 
  onCancel 
}: PromptEditModalProps) {
  const [systemPrompt, setSystemPrompt] = useState('');
  const [userPrompt, setUserPrompt] = useState('');
  const [imagePrompt, setImagePrompt] = useState('');
  const [isModified, setIsModified] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useSimpleToast();

  // Update prompts when request changes
  useEffect(() => {
    if (request) {
      setSystemPrompt(request.prompts.system);
      setUserPrompt(request.prompts.user);
      setImagePrompt(request.prompts.imagePrompt || '');
      setIsModified(false);
    }
  }, [request]);

  // Track modifications
  useEffect(() => {
    if (request) {
      const hasChanges = 
        systemPrompt !== request.prompts.system || 
        userPrompt !== request.prompts.user ||
        imagePrompt !== (request.prompts.imagePrompt || '');
      setIsModified(hasChanges);
    }
  }, [systemPrompt, userPrompt, imagePrompt, request]);

  const handleSend = async () => {
    if (!request) return;

    try {
      setError(null);
      setIsLoading(true);

      // Validate prompts based on request type
      const isImageGeneration = request.metadata.requestType === 'image-generation';
      if (isImageGeneration) {
        if (!imagePrompt.trim()) {
          throw new Error('Image prompt must be provided for image generation requests');
        }
      } else {
        if (!systemPrompt.trim() && !userPrompt.trim()) {
          throw new Error('At least one prompt must be provided');
        }
      }

      const modifiedRequest: AIRequest = {
        ...request,
        prompts: {
          system: systemPrompt,
          user: userPrompt,
          imagePrompt: imagePrompt || undefined,
        },
      };

      onSend(modifiedRequest);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send request';
      setError(errorMessage);
      console.error('Error sending debug request:', err);
      
      toast({
        title: 'Debug Error',
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    try {
      setError(null);
      onCancel();
      onClose();
    } catch (err) {
      console.error('Error cancelling debug request:', err);
      // Still try to close even if there's an error
      onClose();
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: 'Copied',
        description: 'Text copied to clipboard',
      });
    } catch (error) {
      console.warn('Failed to copy to clipboard:', error);
      toast({
        title: 'Copy Failed',
        description: 'Failed to copy text to clipboard',
      });
    }
  };

  const getProviderDisplayName = (provider: string) => {
    switch (provider) {
      case 'gemini':
        return 'Google Gemini';
      case 'openai':
        return 'OpenAI';
      case 'openrouter':
        return 'OpenRouter';
      default:
        return provider;
    }
  };

  const getRequestTypeDisplayName = (type: string) => {
    switch (type) {
      case 'generate':
        return 'Character Generation';
      case 'rewrite':
        return 'Field Rewrite';
      case 'chat-export':
        return 'Chat Export';
      case 'image-generation':
        return 'Image Generation';
      default:
        return type;
    }
  };

  if (!request) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Debug: Edit AI Prompt
            {isModified && (
              <Badge variant="secondary" className="text-xs">
                Modified
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4">
          {/* Error Display */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <div className="font-medium">Debug Error</div>
                <div className="text-sm mt-1">{error}</div>
              </AlertDescription>
            </Alert>
          )}

          {/* Request Metadata */}
          <div className="bg-muted/50 p-3 rounded-lg">
            <div className="flex flex-wrap gap-2 text-sm">
              <Badge variant="outline">
                {getProviderDisplayName(request.provider)}
              </Badge>
              <Badge variant="outline">
                {request.model}
              </Badge>
              <Badge variant="outline">
                {getRequestTypeDisplayName(request.metadata.requestType)}
              </Badge>
              {request.metadata.fieldName && (
                <Badge variant="outline">
                  Field: {request.metadata.fieldName}
                </Badge>
              )}
              {request.metadata.imageType && (
                <Badge variant="outline">
                  Image: {request.metadata.imageType}
                </Badge>
              )}
              {request.metadata.characterType && (
                <Badge variant="outline">
                  Style: {request.metadata.characterType}
                </Badge>
              )}
              {request.metadata.worldDocument && (
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-200 dark:border-blue-800">
                  World: {request.metadata.worldDocument.filename}
                </Badge>
              )}
            </div>
            
            {/* World Document Details */}
            {request.metadata.worldDocument && (
              <div className="mt-3 p-3 bg-blue-50/50 rounded border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
                <div className="text-sm font-medium text-blue-800 dark:text-blue-100 mb-2">World Document Context</div>
                <div className="grid grid-cols-2 gap-2 text-xs text-blue-700 dark:text-blue-300">
                  <div><span className="font-medium">File:</span> {request.metadata.worldDocument.filename}</div>
                  <div><span className="font-medium">Format:</span> {request.metadata.worldDocument.format.toUpperCase()}</div>
                  <div><span className="font-medium">Size:</span> {Math.round(request.metadata.worldDocument.size / 1024)}KB</div>
                  <div><span className="font-medium">Method:</span> {request.metadata.worldDocument.extractionMethod}</div>
                </div>
                <div className="mt-2 text-xs text-blue-600 dark:text-blue-300">
                  This character generation includes world/lore context from the uploaded document.
                </div>
              </div>
            )}
          </div>

          {/* For non-image requests, show system + user prompts */}
          {request.metadata.requestType !== 'image-generation' && (
            <>
              {/* System Prompt */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="system-prompt" className="text-sm font-medium">
                    System Prompt
                  </Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(systemPrompt)}
                    className="h-6 px-2"
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
                <Textarea
                  id="system-prompt"
                  value={systemPrompt}
                  onChange={(e) => setSystemPrompt(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      if (e.ctrlKey || e.shiftKey) return; // allow newline
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  className="min-h-[120px] font-mono text-sm"
                  placeholder="System prompt..."
                />
              </div>

              <Separator />

              {/* User Prompt */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="user-prompt" className="text-sm font-medium">
                    User Prompt
                  </Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(userPrompt)}
                    className="h-6 px-2"
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
                <Textarea
                  id="user-prompt"
                  value={userPrompt}
                  onChange={(e) => setUserPrompt(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      if (e.ctrlKey || e.shiftKey) return; // allow newline
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  className="min-h-[120px] font-mono text-sm"
                  placeholder="User prompt..."
                />
              </div>
            </>
          )}

          {/* Image Prompt - Only show for image generation requests */}
          {request.metadata.requestType === 'image-generation' && (
            <>
              <Separator />
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="image-prompt" className="text-sm font-medium">
                    Image Generation Prompt (final)
                  </Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(imagePrompt)}
                    className="h-6 px-2"
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
                <Textarea
                  id="image-prompt"
                  value={imagePrompt}
                  onChange={(e) => setImagePrompt(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      if (e.ctrlKey || e.shiftKey) return; // allow newline
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  className="min-h-[280px] font-mono text-sm"
                  placeholder="Image generation prompt..."
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter className="flex-shrink-0">
          <span className="mr-auto text-xs text-muted-foreground">
            Press Enter to send • Ctrl+Enter or Shift+Enter for newline
          </span>
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
            className="flex items-center gap-2"
          >
            <X className="h-4 w-4" />
            Cancel Request
          </Button>
          <Button
            onClick={handleSend}
            disabled={isLoading || (
              request.metadata.requestType === 'image-generation' 
                ? !imagePrompt.trim()
                : (!systemPrompt.trim() && !userPrompt.trim())
            )}
            className="flex items-center gap-2"
          >
            <Send className="h-4 w-4" />
            {isLoading ? 'Sending...' : 'Send to AI'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
